import React from 'react';
import { observer } from 'mobx-react';

import { CircularProgress } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { Button } from 'components/Button';

import { useStore } from 'utils/hooks/store';

import { colors } from 'theme/colors';

import { StyledModalActions } from './styles';
import ClickAndDragUpload from '../ClickAndDragUpload';
import ErrorAfterUpload from '../ErrorAfterUpload';
import { useImportVocabulary } from '../../useImportVocabulary';

type UploadCSVStepProps = {
  onClose: () => void;
  isSubmitting: boolean;
  onSumUp: () => void;
  tryAgain: () => void;
};

const UploadCSVStep: React.FC<UploadCSVStepProps> = ({
  onClose,
  isSubmitting,
  onSumUp,
  tryAgain,
}): React.ReactElement => {
  const {
    userList,
    session: { user },
  } = useStore();
  const { vocabulary, loading, failed, retry } = useImportVocabulary();
  const errorView = userList.uploadErrors.length > 0;

  const redirectToHelpCenter = (): void => {
    window.open(user?.helpCenterUrls.addMultipleUsersUrl);
  };

  // Column names come from the org's vocabulary, so a template built mid-fetch would miss columns.
  const renderContent = (): React.ReactElement => {
    if (!isSubmitting && errorView) {
      return (
        <ErrorAfterUpload
          uploadSuccess={userList.usersUploadedSuccessfully}
          uploadErrors={userList.uploadErrors.length}
        />
      );
    }
    if (loading) {
      return (
        <div aria-busy data-testid="import-vocabulary-loading">
          <CircularProgress size="24px" />
        </div>
      );
    }
    if (failed) {
      return (
        <div>
          <p>We could not load the user fields for your organisation.</p>
          <Button variant="outlined" size="large" onClick={retry}>
            Try Again
          </Button>
        </div>
      );
    }
    return <ClickAndDragUpload vocabulary={vocabulary} />;
  };

  return (
    <>
      {renderContent()}
      <StyledModalActions $justify="space-between">
        <Button
          onClick={redirectToHelpCenter}
          startIcon={<HelpOutlineIcon sx={{ color: colors.primary.main, width: '24px', height: '24px' }} />}
        >
          Help
        </Button>
        <div>
          <Button
            type="button"
            variant="outlined"
            id="cancel-button"
            onClick={onClose}
            size="large"
            disabled={isSubmitting}
          >
            Cancel
          </Button>

          {errorView ? (
            <Button
              type="submit"
              id="try-again-button"
              variant="contained"
              size="large"
              sx={{ marginLeft: '8px' }}
              onClick={tryAgain}
            >
              Try Again
            </Button>
          ) : (
            <Button
              type="submit"
              id="next-button"
              variant="contained"
              size="large"
              sx={{ marginLeft: '8px' }}
              onClick={onSumUp}
              disabled={!userList.usersLoaded}
            >
              {isSubmitting && <CircularProgress color="inherit" size="16px" sx={{ marginRight: '8px' }} />}
              Next
            </Button>
          )}
        </div>
      </StyledModalActions>
    </>
  );
};

export default observer(UploadCSVStep);
